import { frameworkBandForLevel } from './difficulty-curve';
/** The configured track, by id. Throws on an unknown id — there are exactly three. */
export function trackConfigFor(config, id) {
    const track = config.tracks.find((t) => t.id === id);
    if (!track)
        throw new Error(`trackConfigFor: no track configured with id "${id}"`);
    return track;
}
/**
 * `(track, ordinal) → { effectiveLevel, variant, holding }`.
 *
 * `ordinal` is 1-based and clamped up: a caller that has not started a track yet
 * holds 0, and asking for its position should answer "puzzle 1" rather than
 * throw. Non-integers are floored for the same reason — this is read on a render
 * path and a `NaN` reaching the generator's seed would be far worse than a
 * silently-rounded one.
 */
export function trackPositionFor(config, id, ordinal) {
    const track = trackConfigFor(config, id);
    const k = Math.max(1, Math.floor(Number.isFinite(ordinal) ? ordinal : 1));
    const span = track.lastLevel - track.firstLevel;
    const ramp = Math.max(1, track.rampLength);
    // A one-puzzle ramp (or a zero-width slice) is the degenerate case: the track
    // is at its ceiling from puzzle 1 and every puzzle after is a variant of it.
    const t = ramp <= 1 ? 1 : Math.min(k, ramp) - 1;
    const denom = ramp <= 1 ? 1 : ramp - 1;
    const holding = k >= ramp;
    const rampLevel = ramp <= 1 ? track.lastLevel : track.firstLevel + Math.round((t / denom) * span);
    return {
        effectiveLevel: holding
            ? holdLevelFor(config, track.lastLevel, track.hold, k - ramp)
            : rampLevel,
        variant: holding ? k - ramp : 0,
        holding,
    };
}
/**
 * The effective level during the endless phase, `steps` puzzles after the hold
 * began (0 = the puzzle that reached the ceiling).
 *
 * `cycle` is a TRIANGLE wave, not a sawtooth, and deliberately starts at the top:
 * the ramp ends on the band's hardest level and the endless phase walks down
 * through the band and back up, so there is no cliff at the boundary and no
 * moment where the game suddenly gets easier by ten levels.
 *
 *   Beginner (ceiling band Easy+, 16–25):  25 24 23 … 17 16 17 18 … 25 24 …
 */
function holdLevelFor(config, lastLevel, mode, steps) {
    if (mode === 'pin')
        return lastLevel;
    const band = frameworkBandForLevel(config, lastLevel);
    const bandMin = Math.max(1, band.levelRange.min);
    const depth = lastLevel - bandMin;
    // A one-level ceiling band has nothing to cycle through.
    if (depth <= 0)
        return lastLevel;
    const period = 2 * depth;
    const j = ((steps % period) + period) % period;
    return j <= depth ? lastLevel - j : lastLevel - (period - j);
}
/**
 * The ordinal at which a track reaches its ceiling. Read by the reward rule
 * (WP-65.B2), which pays in full through the ramp and trickles after — so this
 * is the boundary a player can actually feel, and it needs exactly one
 * definition.
 */
export function rampLengthFor(config, id) {
    return Math.max(1, trackConfigFor(config, id).rampLength);
}
